import {
    createZodFormResolver,
    FormCheckboxField,
    FormCheckboxGroupField,
    FormCounterField,
    FormInputField,
    FormSwitchField,
    LocalizedText,
    useFormControls,
} from '@mews/framework';
import { Stack, StackItem, Typography } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { CounterFormField, CounterUseFor } from '../../CounterForm/formMapping';
import { creditNoteCounterValidationSchema, CreditNoteFormValues } from '../../../constants';
import { CounterFormatPreviewBanner } from '../../CounterFormatPreviewBanner';

interface Props {
    defaultValues: CreditNoteFormValues;
    isEditForm: boolean;
    onSubmit: (values: CreditNoteFormValues) => Promise<void>;
}

export const useCreditNoteCounterForm = ({ defaultValues, onSubmit, isEditForm }: Props) => {
    const { renderFormContent, ...rest } = useFormControls<CreditNoteFormValues>({
        resolver: createZodFormResolver(creditNoteCounterValidationSchema),
        defaultValues,
        onSubmit,
    });

    const { watch } = rest;

    const numberOfDigits = watch(CounterFormField.NumberOfDigits);
    const prefix = watch(CounterFormField.Prefix);
    const startValue = watch(CounterFormField.StartValue);

    return {
        ...rest,
        Form: renderFormContent(
            <Stack vertical spacing="300">
                <Typography textStyle="titleSmallStrong" as="div">
                    <LocalizedText textKey={TextKey.Settings} />
                </Typography>
                <FormInputField
                    required
                    size="medium"
                    placeholder={<LocalizedText textKey={TextKey.CreditNoteCounterTitlePlaceholder} />}
                    helper={<LocalizedText textKey={TextKey.CreditNoteCounterTitleHelper} />}
                    label={<LocalizedText textKey={TextKey.CreditNoteCounterTitleLabel} />}
                    name={CounterFormField.Title}
                />
                <FormSwitchField
                    size="medium"
                    label={<LocalizedText textKey={TextKey.MakeDefault} />}
                    name={CounterFormField.IsDefault}
                />
                <FormCheckboxGroupField
                    required
                    size="medium"
                    label={<LocalizedText textKey={TextKey.CreditNoteCounterUseForLabel} />}
                    options={[
                        {
                            name: CounterFormField.UseFor,
                            value: CounterUseFor.bills,
                            label: <LocalizedText textKey={TextKey.BillPlural} />,
                        },
                        {
                            name: CounterFormField.UseFor,
                            value: CounterUseFor.invoices,
                            label: <LocalizedText textKey={TextKey.InvoicePlural} />,
                        },
                    ]}
                    name={CounterFormField.UseFor}
                />
                <FormInputField
                    required
                    size="medium"
                    helper={<LocalizedText textKey={TextKey.CounterNameHelper} />}
                    placeholder={<LocalizedText textKey={TextKey.CounterNamePlaceholder} />}
                    label={<LocalizedText textKey={TextKey.CounterName} />}
                    name={CounterFormField.Name}
                />
                <Typography textStyle="titleSmallStrong" as="div">
                    <LocalizedText textKey={TextKey.CreditNoteCounterNumberFormattingSectionTitle} />
                </Typography>
                {!isEditForm ? (
                    <FormInputField
                        required
                        size="medium"
                        type="number"
                        helper={<LocalizedText textKey={TextKey.StartValueHelper} />}
                        placeholder={<LocalizedText textKey={TextKey.StartValuePlaceholder} />}
                        label={<LocalizedText textKey={TextKey.StartValue} />}
                        name={CounterFormField.StartValue}
                    />
                ) : null}
                <Stack spacing="300">
                    <StackItem grow>
                        <FormInputField
                            size="medium"
                            placeholder={<LocalizedText textKey={TextKey.CreditNoteCounterPrefixPlaceholder} />}
                            helper={<LocalizedText textKey={TextKey.CreditNoteCounterPrefixHelper} />}
                            label={
                                <Typography as="span" textStyle="bodyMediumStrong">
                                    <LocalizedText textKey={TextKey.CreditNoteCounterPrefixTitle} />
                                </Typography>
                            }
                            name={CounterFormField.Prefix}
                        />
                    </StackItem>
                    <StackItem grow>
                        <FormCounterField
                            min={1}
                            size="medium"
                            required
                            helper={<LocalizedText textKey={TextKey.CreditNoteCounterNumberOfDigitsHelper} />}
                            label={<LocalizedText textKey={TextKey.CreditNoteCounterNumberOfDigitsLabel} />}
                            name={CounterFormField.NumberOfDigits}
                        />
                    </StackItem>
                </Stack>
                <CounterFormatPreviewBanner
                    numberOfDigits={numberOfDigits}
                    prefix={prefix}
                    suffix={null}
                    startValue={startValue}
                />
                <FormInputField
                    label={
                        <Typography as="span" textStyle="bodyMediumStrong">
                            <LocalizedText textKey={TextKey.BillTypeCode} />
                        </Typography>
                    }
                    size="medium"
                    helper={<LocalizedText textKey={TextKey.BillTypeCodeHelper} />}
                    placeholder={<LocalizedText textKey={TextKey.BillTypeCodePlaceholder} />}
                    name={CounterFormField.BillTypeCode}
                />
                <FormCheckboxField size="medium" name={CounterFormField.DisplayCID}>
                    <LocalizedText textKey={TextKey.DisplayCIDOnBillsAndInvoices} />
                </FormCheckboxField>
                {defaultValues?.[CounterFormField.Created] ? (
                    <FormInputField
                        label={<LocalizedText textKey={TextKey.Created} />}
                        name={CounterFormField.Created}
                        readOnly
                        disabled
                        size="medium"
                    />
                ) : null}

                {defaultValues?.[CounterFormField.Updated] ? (
                    <FormInputField
                        label={<LocalizedText textKey={TextKey.Updated} />}
                        name={CounterFormField.Updated}
                        readOnly
                        disabled
                        size="medium"
                    />
                ) : null}
            </Stack>
        ),
    };
};
